import React, {useEffect, useMemo, useState} from 'react';
import {ArrowRight, Search as SearchIcon} from '@mui/icons-material';
import {Box, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {buildFunctionalEnrichmentSampleGroups, DefaultChIPSampleGroup, FunctionalEnrichmentSampleGroup} from 'src/utils/chip';
import {useTrovoConfig} from 'src/utils/config';
import {isDemoExperiment} from 'src/utils/demo-data';
import {
    ActionBar,
    ActionItemStyled,
    ActionTextStyled,
    ActionTitleStyled,
    FiltersContainer,
    FiltersWrapper,
    Search,
    SearchIconWrapper,
    StyledInputBase
} from '../rna/steps/step1/ExpandableTable.styles';
import SampleGroupTable from './SampleGroupTable';

interface SampleGroupSelectorProps {
    formData: FEFormData;
    setFormData: React.Dispatch<React.SetStateAction<FEFormData>>;
    handleNext: (valid: boolean) => void;
    data: any[];
    error: string;
    defaultChIPSampleGroups: DefaultChIPSampleGroup[];
    loadingDefaultChIPSampleGroups: boolean;
}

const SampleGroupSelector = ({
    formData,
    setFormData,
    handleNext,
    data,
    error,
    defaultChIPSampleGroups,
    loadingDefaultChIPSampleGroups
}: SampleGroupSelectorProps) => {
    const navigate = useNavigate();
    const {user} = useTrovoConfig();
    const [searchTerm, setSearchTerm] = useState('');
    const groups = useMemo(() => buildFunctionalEnrichmentSampleGroups(data, defaultChIPSampleGroups), [data, defaultChIPSampleGroups]);
    const selectedIds = useMemo(() => new Set(formData.sample_ids as string[]), [formData.sample_ids]);
    const selectedValidGroups = useMemo(
        () => groups.filter(group => !group.disabled && group.samples.every(sample => selectedIds.has(sample.id))),
        [groups, selectedIds]
    );
    const normalizedSelectedIds = useMemo(
        () => new Set(selectedValidGroups.flatMap(group => group.samples.map(sample => sample.id))),
        [selectedValidGroups]
    );
    const hasInvalidSelectedIds = (formData.sample_ids as string[]).some(id => !normalizedSelectedIds.has(id));
    const selectionValid = selectedValidGroups.length > 0 && !hasInvalidSelectedIds;

    useEffect(() => {
        if (!hasInvalidSelectedIds) return;
        setFormData(prev => ({
            ...prev,
            sample_ids: (prev.sample_ids as string[]).filter(id => normalizedSelectedIds.has(id))
        }));
    }, [hasInvalidSelectedIds, normalizedSelectedIds, setFormData]);

    useEffect(() => {
        handleNext(!loadingDefaultChIPSampleGroups && !error && selectionValid);
    }, [error, handleNext, loadingDefaultChIPSampleGroups, selectionValid]);

    const toggleGroup = (group: FunctionalEnrichmentSampleGroup) => {
        const groupIds = group.samples.map(sample => sample.id);
        setFormData(prev => {
            const selected = groupIds.every(id => (prev.sample_ids as string[]).includes(id));
            return {
                ...prev,
                sample_ids: selected
                    ? (prev.sample_ids as string[]).filter(id => !groupIds.includes(id))
                    : Array.from(new Set([...(prev.sample_ids as string[]), ...groupIds]))
            };
        });
    };

    const toggleAll = (visibleGroups: FunctionalEnrichmentSampleGroup[]) => {
        const groupIds = visibleGroups.flatMap(group => group.samples.map(sample => sample.id));
        setFormData(prev => {
            const currentIds = prev.sample_ids as string[];
            const allSelected = groupIds.every(id => currentIds.includes(id));
            return {
                ...prev,
                sample_ids: allSelected
                    ? currentIds.filter(id => !groupIds.includes(id))
                    : Array.from(new Set([...currentIds, ...groupIds]))
            };
        });
    };

    return (
        <>
            <ActionBar>
                <ActionItemStyled
                    sx={{cursor: 'pointer', bgcolor: 'surface-container-lowest'}}
                    onClick={() => navigate(`/samples/${data?.[0]?.experiment_id}`)}>
                    <div>
                        <ActionTitleStyled>Manage Samples</ActionTitleStyled>
                        <ActionTextStyled>Edit or review sample statuses.</ActionTextStyled>
                    </div>
                    <ArrowRight sx={{color: 'black'}} />
                </ActionItemStyled>
            </ActionBar>

            <Box sx={{bgcolor: 'surface-container-lowest', padding: '2%', borderRadius: '10px'}}>
                <FiltersContainer>
                    <FiltersWrapper>
                        <Search>
                            <SearchIconWrapper>
                                <SearchIcon />
                            </SearchIconWrapper>
                            <StyledInputBase
                                placeholder="Search…"
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
                                inputProps={{'aria-label': 'search'}}
                            />
                        </Search>
                    </FiltersWrapper>
                </FiltersContainer>

                {loadingDefaultChIPSampleGroups ? (
                    <Typography sx={{py: 3}}>Loading sample groups from the selected default analysis...</Typography>
                ) : (
                    <SampleGroupTable
                        groups={groups}
                        selectedSampleIds={formData.sample_ids as string[]}
                        searchTerm={searchTerm}
                        selectionDisabled={isDemoExperiment(data?.[0]?.experiment_id) || user.cb_item_price_id?.includes('demo') === true}
                        onToggleGroup={toggleGroup}
                        onToggleAll={toggleAll}
                    />
                )}
            </Box>
        </>
    );
};

export default SampleGroupSelector;
